/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import FilterModal from "./FilterModal";
import type { ApiResponse, FilterCondition, FilterQuery } from "./FilterModal";
import IconComponent from "@CubeComponents/icons";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import dayjs, { type Dayjs } from "dayjs";
import {
  applyMultiSelectFilter,
  callRemoteSource,
  getConditionMet,
  type FilterType,
  type QueryOperator,
} from "../filterUtils";
import { untranslated, useTranslation } from "@i18n";

/* --------------------------------------------
   Types
   -------------------------------------------- */

export interface FilterColumn {
  /** Unique field key */
  field: string;
  /** Display name for the column */
  name: string;
  /** Filter type: "Text" | "Date" | "Number" | "MultiSelect" | "FileSize" */
  filterType: string;
  /** Unique id (defaults to field if not provided) */
  id?: string;
  /** Disable filtering for this column */
  disableFiltering?: boolean;
}

interface RemoteDataSourceConfig {
  /** API endpoint URL, or a function that performs the remote call and returns what the modal reads */
  url: string | ((body: { value: FilterQuery }) => Promise<ApiResponse>);
  /** Base query object to send with the remote call (e.g. { params: { ... }, skip: 0, take: 50 }) */
  query?: FilterQuery;
  /** Transform raw API response before it reaches the FilterModal (e.g. extract nested array) */
  beforeDataBound?: (data: unknown) => unknown[];
  /** Transform filter conditions before they are passed to onFilterChange (e.g. build a where clause) */
  getUpdatedFilterConditions?: (filterConditionsObj: Record<string, unknown>) => unknown;
}

/** A single saved-view filter condition keyed by field in the conditions object */
export interface FilterConditionEntry {
  id?: string;
  type?: string;
  option?: string;
  text?: unknown;
  logic?: string;
}

/**
 * Used for both of this file's untyped inputs: a `TItem` row, which is only constrained by the
 * caller and so is checked before being indexed by field, and a saved condition value.
 */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/**
 * The saved-view blob as it arrives over the network. `layoutView.data.filterConditions` is a
 * free-form document (`z.record(z.string(), ganttViewFilterConditionSchema)` in
 * packages/server/layoutView/layoutView.schema.ts) that the Gantt hands straight to this component,
 * so an entry can be missing or null however the prop declares it — the reads below stay guarded.
 */
type SavedFilterConditions = Record<string, FilterConditionEntry | null | undefined>;

/**
 * The object arm of a condition value: the wrapper a Date or FileSize condition stores. Mirrors
 * FilterModal's private `FilterValueObject`; every key is optional there too, so the two shapes stay
 * assignable in both directions.
 */
type FilterValueWrapper = {
  date?: Dayjs | string | null;
  startDate?: Dayjs | string | null;
  endDate?: Dayjs | string | null;
  size?: string | number | null;
  unit?: string;
};

const isWrapperDate = (value: unknown): value is Dayjs | string | null | undefined =>
  value === null || value === undefined || typeof value === "string" || dayjs.isDayjs(value);

const isFilterValueWrapper = (value: Record<string, unknown>): value is FilterValueWrapper =>
  isWrapperDate(value.date) &&
  isWrapperDate(value.startDate) &&
  isWrapperDate(value.endDate) &&
  (value.size === null ||
    value.size === undefined ||
    typeof value.size === "string" ||
    typeof value.size === "number") &&
  (value.unit === undefined || typeof value.unit === "string");

/**
 * `text` is whatever the saved view persisted — the schema above declares it `z.unknown()` — so the
 * arms `FilterCondition["value"]` allows are recognised structurally rather than asserted. A Date or
 * FileSize condition keeps its `{ date | startDate | endDate | size | unit }` wrapper object
 * unchanged, a MultiSelect keeps its selected strings (the modal builds its options with `String()`,
 * so strings are all it ever writes), and a value the modal has no input for reads as "nothing
 * entered" — which is what its own `toValueObject` / `toSelectionString` erase such a value to.
 */
const toConditionValue = (value: unknown): FilterCondition["value"] => {
  if (value === null || value === undefined || typeof value === "string" || typeof value === "number") return value;
  if (Array.isArray(value)) {
    const entries: unknown[] = value;
    return entries.filter((entry): entry is string => typeof entry === "string");
  }
  if (isRecord(value) && isFilterValueWrapper(value)) return value;
  return undefined;
};

/**
 * The filter type and operator reach {@link FilterButton} as plain strings — a saved view can carry
 * any `type` / `option` string — so both are checked against the unions `getConditionMet` accepts
 * instead of being asserted into them.
 */
const FILTER_TYPES = new Set<string>(["Text", "Date", "Number", "MultiSelect", "FileSize"] satisfies FilterType[]);

const QUERY_OPERATORS = new Set<string>([
  "startswith",
  "endswith",
  "empty",
  "equal",
  "contains",
  "like",
  "doesnotstartwith",
  "doesnotendwith",
  "doesnotcontain",
  "notequal",
  "notempty",
  "notnull",
  "null",
  "greaterthan",
  "greaterthanorequal",
  "lessthan",
  "lessthanorequal",
  "between",
] satisfies QueryOperator[]);

const isFilterType = (value: string): value is FilterType => FILTER_TYPES.has(value);

const isQueryOperator = (value: string): value is QueryOperator => QUERY_OPERATORS.has(value);

const extractFilterIds = (obj: SavedFilterConditions): string[] =>
  Object.values(obj)
    .map((c) => c?.id)
    .filter((id): id is string => Boolean(id));

/**
 * `TItem` is the caller's row type. It exists so `data` and `getFieldValue` agree: call sites
 * annotate their accessor with their own row type (e.g. the Gantt's `Task`), which no fixed
 * parameter type would accept.
 */
export interface FilterButtonProps<TItem = unknown> {
  /** Column definitions available for filtering */
  columns: FilterColumn[];
  /** The local dataset to filter against. Used for autocomplete suggestions in local mode. */
  data?: TItem[];
  /** Called whenever filter conditions change. Receives the conditions object keyed by field. */
  onFilterChange: (filterConditionsObject: Record<string, FilterConditionEntry>) => void;
  /** Called when filter predicates change (remote mode). Receives predicates for building API where clauses. */
  onFilterPredicatesChange?: (predicates: unknown) => void;
  /** Optional: custom function to extract a field value from a data item */
  getFieldValue?: (item: TItem, field: string) => unknown;
  /** Optional: render a custom trigger instead of the default filter icon button */
  renderTrigger?: (props: {
    onClick: (e: React.MouseEvent<HTMLElement>) => void;
    hasActiveFilters: boolean;
  }) => React.ReactNode;
  /** Optional: remote data source config. When provided, filter suggestions are fetched from the API. */
  remoteDataSource?: RemoteDataSourceConfig;
  /** Optional: ref to the grid instance (needed for remote filtering to refresh data) */
  gridRef?: React.RefObject<unknown>;
  /** Optional: seed the derived filterConditionsObject (used to restore filters from a saved view) */
  initialFilterConditionsObject?: Record<string, FilterConditionEntry>;
}

/* --------------------------------------------
   Component
   -------------------------------------------- */

export default function FilterButton<TItem = unknown>({
  columns,
  data = [],
  onFilterChange,
  onFilterPredicatesChange,
  getFieldValue,
  renderTrigger,
  remoteDataSource,
  gridRef: externalGridRef,
  initialFilterConditionsObject,
}: FilterButtonProps<TItem>) {
  const { t } = useTranslation();

  const isRemote = !!remoteDataSource;

  // FilterModal's row type declares `field` / `operator` as required. A saved-view entry can be
  // missing either (the modal itself guards with `if (!condition.field)`), so the mismatch is
  // asserted away here rather than defaulted — a default would change what the modal renders.
  const deriveConditionsFromObject = (obj?: SavedFilterConditions): FilterCondition[] => {
    if (!obj) return [];
    return Object.values(obj).map((c) => ({
      field: c?.id!,
      operator: c?.option!,
      value: toConditionValue(c?.text),
      logic: c?.logic || "and",
      id: c?.id,
    }));
  };

  const [conditions, setConditions] = useState<FilterCondition[]>(
    deriveConditionsFromObject(initialFilterConditionsObject),
  );
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<string[]>(
    initialFilterConditionsObject ? extractFilterIds(initialFilterConditionsObject) : [],
  );
  const [filterConditionsObject, setFilterConditionsObject] = useState<Record<string, FilterConditionEntry>>(
    initialFilterConditionsObject ?? {},
  );
  const [filterPredicates, setFilterPredicates] = useState<unknown>("");
  const [tempCondition, setTempCondition] = useState<FilterCondition | null>(null);
  // KNOWN BUG, preserved verbatim: initialised to `{ current: null }`, so `.current` is an object
  // WRAPPING another `current` until the grid replaces it. The initialiser is left untouched.
  const internalGridRef = useRef<unknown>({ current: null });
  const gridRef = externalGridRef || internalGridRef;
  const seededConditionsRef = useRef<Record<string, FilterConditionEntry> | null>(null);
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  useEffect(() => {
    const obj = initialFilterConditionsObject ?? {};
    seededConditionsRef.current = obj;
    setFilterConditionsObject(obj);
    setConditions(deriveConditionsFromObject(initialFilterConditionsObject));
    setAppliedFilters(extractFilterIds(obj));
  }, [initialFilterConditionsObject]);

  // Propagate filter changes to the consumer
  useEffect(() => {
    if (seededConditionsRef.current === filterConditionsObject) {
      seededConditionsRef.current = null;
      return;
    }
    onFilterChangeRef.current(filterConditionsObject);
  }, [filterConditionsObject]);

  // Propagate filter predicates to the consumer (remote mode)
  useEffect(() => {
    if (isRemote && onFilterPredicatesChange) {
      onFilterPredicatesChange(filterPredicates);
    }
  }, [filterPredicates]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setTempCondition(null);
    setIsOpen(false);
  }, []);

  const columnDefinitions = useMemo(
    () =>
      columns.map((col) => ({
        field: col.field,
        // The modal's column shape brands `name` as translated; callers already hand this component
        // display names they own, so it is marked untranslated rather than re-translated.
        name: untranslated(col.name),
        filterType: col.filterType,
        id: col.id || col.field,
        disableFiltering: col.disableFiltering,
      })),
    [columns],
  );

  // Local apply filter function — reuses getConditionMet from filterUtils (same as SlickGrid1's applyFilter)
  const applyFilterFunction = useCallback(
    (dataset: TItem[], field: string, operator: string, text: string, filterType: string) => {
      // A filter type outside the five `getConditionMet` knows misses its lookup map and matches
      // nothing, which is what the empty result reproduces.
      if (!isFilterType(filterType)) return [];
      const type: FilterType = filterType;
      // MultiSelect is the one type whose comparison ignores the operator, so it is dispatched
      // directly — `getConditionMet` would forward to the same helper. For the other four an
      // unrecognised operator misses their lookup map, i.e. matches nothing.
      const queryOperator = isQueryOperator(operator) ? operator : null;
      return dataset.filter((item: TItem) => {
        let fieldValue: unknown;
        if (getFieldValue) {
          fieldValue = getFieldValue(item, field);
        } else if (isRecord(item)) {
          fieldValue = item[field];
        }
        if (type === "MultiSelect") return applyMultiSelectFilter(fieldValue, text);
        if (!queryOperator) return false;
        return getConditionMet(type, fieldValue, queryOperator, text);
      });
    },
    [getFieldValue],
  );

  // Remote call handler — reuses callRemoteSource from filterUtils (same as SlickGrid1's callRemote)
  // `callRemoteSource` cannot infer a result type from a plain URL string, so the response shape the
  // modal expects is stated here instead.
  const callRemote = useCallback(
    async (_source: string, body: { value: FilterQuery }): Promise<ApiResponse> => {
      if (!remoteDataSource) return { result: [] };
      return callRemoteSource<{ value: FilterQuery }, ApiResponse>(remoteDataSource.url, body);
    },
    [remoteDataSource],
  );

  const hasActiveFilters = conditions.length > 0;

  // FilterModal takes the source as a *string*, but only tests it for truthiness and passes it back
  // to `callRemote` — which ignores that argument and calls `remoteDataSource.url` itself. A
  // function-valued url has no string form, so remote mode is flagged with a marker instead.
  let modalRemoteSource: string | null = null;
  if (remoteDataSource) {
    modalRemoteSource = typeof remoteDataSource.url === "string" ? remoteDataSource.url : "[function]";
  }

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ onClick: handleClick, hasActiveFilters })
      ) : (
        <ToolTipComponent content={t("Filtering")}>
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleClick} iconButton={true}>
            <IconComponent name="Filter" width="1.5rem" height="1.5rem" />
            {hasActiveFilters && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  insetInlineEnd: 0,
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "6.25rem",
                  background: "var(--Notification-notification-primary, #6BBBE3)",
                  transform: "translate(-50%, 50%)",
                  zIndex: 10,
                }}
              />
            )}
          </ButtonComponent>
        </ToolTipComponent>
      )}

      {isOpen && (
        <FilterModal
          isOpen={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          columnDefinitions={columnDefinitions}
          conditions={conditions}
          setConditions={setConditions}
          remoteDataSource={modalRemoteSource}
          callRemote={callRemote}
          // Left undefined when the config has none: the modal then reads `result` / `data.result`
          // off the response itself. The identity function that used to stand in here handed it the
          // whole response envelope as if that were the row array.
          beforeDataBound={remoteDataSource?.beforeDataBound}
          datasetForFetchingOptions={data}
          query={remoteDataSource ? remoteDataSource.query || null : null}
          localData={!isRemote}
          getUpdatedFilterConditions={
            remoteDataSource?.getUpdatedFilterConditions || ((obj: Record<string, unknown>) => obj)
          }
          applyFilterFunction={applyFilterFunction}
          gridRef={gridRef}
          setAppliedFilters={setAppliedFilters}
          appliedFilters={appliedFilters}
          setFilterPredicates={setFilterPredicates}
          setFilterConditionsObject={setFilterConditionsObject}
          tempCondition={tempCondition}
          setTempCondition={setTempCondition}
        />
      )}
    </>
  );
}
